import React, { useRef, useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Platform,
  Dimensions,
  Animated,
  Easing,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import LoadingSpinner from '../components/LoadingSpinner';
import { Image } from 'react-native';
import { BlurView } from 'expo-blur';
import Svg, { Path, Defs, LinearGradient as SvgLinearGradient, Stop, Mask } from 'react-native-svg';
import { Ionicons } from '@expo/vector-icons';
import NavigationHeader from '../components/NavigationHeader';
import { ThemeGradientOverlay, ThemeGlassBackground } from '../components/ThemeGradients';
import { useTheme } from '../context/ThemeContext';
import { useNavigation } from '../context/NavigationContext';
import { useTicker } from '../context/TickerContext';
import { useBackgroundQuery } from '../context/BackgroundQueryContext';
import { theme, useBreakpoint } from '../constants/theme';
import { auth, db } from '../firebaseConfig';
import { collection, query as firestoreQuery, where, getDocs, orderBy, limit, deleteDoc, doc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

const getViewport = () => Dimensions.get('window');


// Screenshot Icon Component
export const ScreenshotIcon = ({ size = 23 }) => (
  <View style={{ width: size, height: size, borderRadius: 4, justifyContent: 'center', alignItems: 'center' }}>
    <Ionicons name="images-outline" size={size} color="white" />
  </View>
);

// Small Logo Icon Component
export const SmallLogoIcon = ({ size = 19 }) => (
  <Image source={require('../assets/icon.png')} style={{ width: size, height: size, borderRadius: 6 }} resizeMode="contain" />
);

// Search Input Component
const SearchInput = ({ onNavigate, valueProp, onValueChange, onSearchStart, onExecuteSearch }) => {
  const [isFocused, setFocused] = useState(false);
  const [value, setValue] = useState(valueProp || '');
  const focusT = useRef(new Animated.Value(0)).current;
  const borderRadiusT = useRef(new Animated.Value(10)).current;
  const { isGradientEnabled, enableGradient, disableGradient } = useTheme();
  const [typedPlaceholder, setTypedPlaceholder] = useState('');
  const placeholderIdxRef = useRef(0);
  const typeIdxRef = useRef(0);
  const directionRef = useRef(1); // 1 typing, -1 deleting
  const timerRef = useRef(null);
  const samples = [
    'best nba sgp tonight',
    'lakers -3.5 and lebron 9.5+ ast',
    'under 220.5 and jokic 10+ reb',
    'same game parlay warriors today',
    'nfl sgp chiefs tomorrow'
  ];
  const [errorMsg, setErrorMsg] = useState('');
  
  const onFocus = () => {
    setFocused(true);
    enableGradient('searchInput');
    // Stop any running animations and get current value
    focusT.stopAnimation();
    borderRadiusT.stopAnimation();
    
    // Calculate remaining duration based on current value
    const currentFocus = focusT._value || 0;
    const currentBorderRadius = borderRadiusT._value || 10;
    const remainingDuration = 2000 * (1 - currentFocus);
    
    console.log('🎯 Starting focus animation:', { 
      currentFocus, 
      currentBorderRadius, 
      remainingDuration,
      targetFocus: 1,
      targetBorderRadius: 999
    });
    
    // Ensure minimum duration for smooth animation
    const finalDuration = Math.max(remainingDuration, 500);
    
    console.log('🚀 Starting animations with duration:', finalDuration);
    
    // Start focus animation
    const focusAnimation = Animated.timing(focusT, { 
      toValue: 1, 
      duration: finalDuration, 
      easing: Easing.bezier(0.22, 1, 0.36, 1), 
      useNativeDriver: false 
    });
    
    // Start border radius animation
    const borderRadiusAnimation = Animated.timing(borderRadiusT, { 
      toValue: 999, 
      duration: finalDuration, 
      easing: Easing.bezier(0.22, 1, 0.36, 1), 
      useNativeDriver: false 
    });
    
    // Run animations in parallel
    Animated.parallel([focusAnimation, borderRadiusAnimation]).start();
  };
  
  const onBlur = () => {
    setFocused(false);
    // Stop any running animations and get current value
    focusT.stopAnimation();
    borderRadiusT.stopAnimation();
    
    // Calculate remaining duration based on current value
    const currentFocus = focusT._value || 0;
    const remainingDuration = 2000 * currentFocus;
    
    console.log('🎯 Starting blur animation:', { currentFocus, remainingDuration });
    
    Animated.parallel([
      Animated.timing(focusT, { toValue: 0, duration: remainingDuration, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: false }),
      Animated.timing(borderRadiusT, { toValue: 10, duration: remainingDuration, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: false })
    ]).start(() => {
      // Small delay to ensure animation is completely finished
      setTimeout(() => {
        disableGradient('searchInput');
      }, 50);
    });
  };
  
  const handleSubmit = () => {
    const q = (valueProp !== undefined ? valueProp : value).trim();
    if (q) {
      try { if (Platform.OS === 'web') window.sessionStorage.setItem('sgp:lastQuery', q); } catch {}
      // Notify parent that search is starting (for loading overlay)
      if (onSearchStart) onSearchStart(q);
      // If custom execute handler provided (for background query support), use it
      if (onExecuteSearch) {
        onExecuteSearch(q);
      } else {
      // Small delay to show loading state before navigation
      setTimeout(() => {
      onNavigate('SgpFlow', { payload: { query: q } });
      }, 100);
      }
    }
  };

  useEffect(() => {
    if (valueProp !== undefined && valueProp !== value) setValue(valueProp);
  }, [valueProp]);
  
  // Typewriter placeholder when not focused and empty
  useEffect(() => {
    const shouldType = !isFocused && !(valueProp !== undefined ? valueProp : value);
    if (!shouldType) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setTypedPlaceholder('');
      return;
    }
    if (timerRef.current) return;
    timerRef.current = setInterval(() => {
      const curSample = samples[placeholderIdxRef.current % samples.length];
      const i = typeIdxRef.current + directionRef.current;
      if (i <= 0) {
        directionRef.current = 1;
        placeholderIdxRef.current = (placeholderIdxRef.current + 1) % samples.length;
        typeIdxRef.current = 0;
        setTypedPlaceholder('');
      } else if (i >= curSample.length) {
        // ensure the last character is visible, then pause before deleting
        typeIdxRef.current = curSample.length;
        setTypedPlaceholder(curSample);
        directionRef.current = 0;
        setTimeout(() => { directionRef.current = -1; }, 2000);
      } else {
        typeIdxRef.current = i;
        setTypedPlaceholder(curSample.slice(0, Math.min(i, curSample.length)));
      }
    }, 70);
    return () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  }, [isFocused, valueProp, value]);
  
  return (
    <View style={styles.searchContainer}>
      <Animated.View style={[styles.searchAnimatedWrap]}>
        <Animated.View style={{ 
          borderRadius: borderRadiusT, 
          overflow: 'hidden'
        }}>
          <ThemeGlassBackground 
            opacity={focusT} 
            borderRadius={borderRadiusT} 
            enabled={isGradientEnabled('searchInput')} 
          />
          <ThemeGradientOverlay 
            opacity={focusT} 
            borderRadius={borderRadiusT} 
            enabled={isGradientEnabled('searchInput')} 
          />
        </Animated.View>
        <Animated.View style={[
          styles.searchInput, 
          { 
            borderRadius: borderRadiusT,
            backgroundColor: 'rgba(0,0,0,0.3)',
            backdropFilter: 'blur(10px)'
          }
        ]}> 
        <TextInput
          placeholder={isFocused || value ? '' : typedPlaceholder || 'Type a bet idea…'}
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
          style={styles.searchText}
          value={valueProp !== undefined ? valueProp : value}
          onChangeText={(t) => { setValue(t); onValueChange && onValueChange(t); }}
          onFocus={onFocus}
          onBlur={onBlur}
          onSubmitEditing={handleSubmit}
          returnKeyType="search"
          selectionColor={Platform.OS === 'web' ? undefined : 'rgba(255,255,255,0.9)'}
        />
        <View style={styles.iconsContainer}>
          <TouchableOpacity onPress={async () => {
            try {
              // Immediately navigate to SgpFlow with photo upload indicator
              onNavigate('SgpFlow', { payload: { photoUploadInProgress: true } });
              
              const pick = await (await import('expo-document-picker')).getDocumentAsync({ type: 'image/*' });
              if (pick.canceled || !pick.assets || !pick.assets.length) {
                // User canceled, go back
                onNavigate('Home');
                return;
              }
              
              const file = pick.assets[0];
              
              // Store progress callback for SGP Flow to listen to
              if (Platform.OS === 'web') {
                try {
                  window.photoUploadProgress = { step: 'uploading', message: 'Uploading photo...', progress: 10 };
                } catch {}
              }
              
              const resp = await fetch(file.uri);
              const blob = await resp.blob();
              const toBase64 = (b) => new Promise((resolve, reject) => {
                try {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(String(reader.result));
                  reader.onerror = reject;
                  reader.readAsDataURL(b);
                } catch (e) { reject(e); }
              });
              
              if (Platform.OS === 'web') {
                try {
                  window.photoUploadProgress = { step: 'converting', message: 'Processing image...', progress: 30 };
                } catch {}
              }
              
              const dataUrl = await toBase64(blob);
              const Constants = (await import('expo-constants')).default;
              
              // Determine functions URL: use emulator on localhost, production otherwise
              const isLocalhost = Platform.OS === 'web' && 
                  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
              
              const base = isLocalhost
                  ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1' // Emulator
                  : (
                      (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                      || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                      || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
                      || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
                      || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net' // Production
                  );
              console.log('[HomeScreen] Functions base URL:', base, '(localhost:', isLocalhost, ')');
              
              if (Platform.OS === 'web') {
                try {
                  window.photoUploadProgress = { step: 'analyzing', message: 'Analyzing photo with AI...', progress: 50 };
                } catch {}
              }
              
              // Call vision endpoint directly with base64 (no Storage bucket required)
              const res = await fetch(`${base}/nlOddsFromImage`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ base64: dataUrl, defaults: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, now: new Date().toISOString() } }) });
              const text = await res.text();
              let body; try { body = JSON.parse(text); } catch { body = {}; }
              const derived = (body && body.prompt) ? String(body.prompt) : '';
              const promotion = body?.promotion || null;
              
              if (Platform.OS === 'web') {
                try {
                  const foundMsg = derived ? `Found: ${derived}` : 'Analyzing...';
                  window.photoUploadProgress = { step: 'found', message: foundMsg, progress: 75, derived, promotion };
                } catch {}
              }
              
              // If a promotion was detected, save it to user's promotions
              if (promotion && promotion.type) {
                try {
                  const { auth, db } = await import('../firebaseConfig');
                  const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
                  const user = auth?.currentUser;
                  if (user) {
                    await addDoc(collection(db, 'users', user.uid, 'promotions'), {
                      ...promotion,
                      screenshotUrl: dataUrl.slice(0, 500), // Store truncated for reference
                      query: derived,
                      createdAt: serverTimestamp(),
                      used: false
                    });
                    console.log('[Screenshot] Saved promotion:', promotion);
                  }
                } catch (saveErr) {
                  console.warn('[Screenshot] Failed to save promotion:', saveErr);
                  // Non-fatal, continue
                }
              }
              
              if (derived) {
                try { if (Platform.OS === 'web') window.sessionStorage.setItem('sgp:lastQuery', derived); } catch {}
                
                if (Platform.OS === 'web') {
                  try {
                    window.photoUploadProgress = { step: 'searching', message: 'Searching for bets...', progress: 90, derived, promotion };
                  } catch {}
                }
                
                // Trigger the actual query execution
                if (Platform.OS === 'web') {
                  try {
                    window.triggerPhotoQuery = { query: derived, promotion };
                  } catch {}
                }
              } else {
                // Navigation back to home with error will be handled by SgpFlow
                if (Platform.OS === 'web') {
                  try {
                    window.photoUploadProgress = { step: 'error', message: 'We couldn\'t read anything useful in the image. Try a clearer photo.', progress: 0 };
                  } catch {}
                }
              }
            } catch (e) {
              console.log('Photo flow error', e?.message || e);
              if (Platform.OS === 'web') {
                try {
                  window.photoUploadProgress = { step: 'error', message: String(e?.message || 'Upload failed'), progress: 0 };
                } catch {}
              }
            }
          }} activeOpacity={0.7}>
            <ScreenshotIcon size={28} />
          </TouchableOpacity>
        </View>
      </Animated.View>
      {errorMsg ? (
        <View style={{ marginTop: 8 }}>
          <Text style={{ color: '#ff6b6b', fontSize: 13 }}>{errorMsg}</Text>
        </View>
      ) : null}
    </Animated.View>
    </View>
  );
};

// Add Leg Button Component
const AddLegButton = ({ onPress }) => {
  const glowT = useRef(new Animated.Value(0)).current;
  const { isGradientEnabled, enableGradient, disableGradient } = useTheme();
  const handleIn = () => {
    enableGradient('searchButton');
    // Stop any running animations and get current value
    glowT.stopAnimation();
    
    // Calculate remaining duration based on current value
    const currentGlow = glowT._value || 0;
    const remainingDuration = 1200 * (1 - currentGlow);
    
    Animated.timing(glowT, { toValue: 1, duration: remainingDuration, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: false }).start();
  };
  const handleOut = () => {
    // Stop any running animations and get current value
    glowT.stopAnimation();
    
    // Calculate remaining duration based on current value
    const currentGlow = glowT._value || 0;
    const remainingDuration = 1200 * currentGlow;
    
    Animated.timing(glowT, { toValue: 0, duration: remainingDuration, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: false }).start(() => {
      // Small delay to ensure animation is completely finished
      setTimeout(() => {
        disableGradient('searchButton');
      }, 50);
    });
  };
  return (
    <TouchableOpacity style={styles.buttonContainer} activeOpacity={0.85} onPress={onPress} {...(Platform.OS === 'web' ? { onMouseEnter: handleIn, onMouseLeave: handleOut } : {})}>
      <BlurView intensity={60} tint="dark" style={styles.button}>
        <ThemeGradientOverlay 
          opacity={glowT} 
          borderRadius={styles.button.borderRadius || 12} 
          enabled={isGradientEnabled('searchButton')} 
        />
        <Text style={styles.buttonPlus}>→</Text>
        <Text style={styles.buttonText}>Search</Text>
      </BlurView>
    </TouchableOpacity>
  );
};

// Main Home Screen Component
export default function HomeScreen() {
  // Navigation context
  const { navigateTo } = useNavigation();
  const [searchValue, setSearchValue] = useState('');
  const { user, userProfile } = useAuth();
  const { addBackgroundQuery, canAddBackgroundQuery } = useBackgroundQuery();
  const [savedQueries, setSavedQueries] = useState([]);
  const [showSavedQueries, setShowSavedQueries] = useState(false);
  const [isSearching, setIsSearching] = useState(false); // Loading state for search
  const [searchQuery, setSearchQuery] = useState(''); // Current query being searched
  
  // Check if background query mode is enabled
  const backgroundQueryMode = userProfile?.preferences?.backgroundQueryMode === true;
  
  // Debug: Log background mode setting (can remove after debugging)
  useEffect(() => {
    if (userProfile) {
      console.log('[HomeScreen] backgroundQueryMode:', backgroundQueryMode, 'preferences:', userProfile?.preferences);
    }
  }, [userProfile, backgroundQueryMode]);
  
  // Ticker context for header height calculation
  const { isTickerEnabled, totalHeaderHeight } = useTicker();
  
  // Animation values
  const pageOpacity = useRef(new Animated.Value(0)).current;
  const pageTranslateY = useRef(new Animated.Value(20)).current;
  const [isExiting, setIsExiting] = useState(false);

  const contentGlowT = useRef(new Animated.Value(0)).current;
  const contentGlassT = useRef(new Animated.Value(0)).current;
  const { isGradientEnabled, enableGradient, disableGradient } = useTheme();
  const bp = useBreakpoint();
  const [titleSize, setTitleSize] = useState(Platform.OS === 'web' ? theme.layout.titleBase(bp.width) : 64);
  const onContentIn = () => {
    enableGradient('mainContent');
    // Stop any running animations and get current value
    contentGlowT.stopAnimation();
    
    // Calculate remaining duration based on current value
    const currentGlow = contentGlowT._value || 0;
    const remainingDuration = 2000 * (1 - currentGlow);
    
    Animated.timing(contentGlowT, { toValue: 1, duration: remainingDuration, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: false }).start();
  };
  const onContentOut = () => {
    // Stop any running animations and get current value
    contentGlowT.stopAnimation();
    
    // Calculate remaining duration based on current value
    const currentGlow = contentGlowT._value || 0;
    const remainingDuration = 2000 * currentGlow;
    
    Animated.timing(contentGlowT, { toValue: 0, duration: remainingDuration, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: false }).start(() => {
      // Small delay to ensure animation is completely finished
      setTimeout(() => {
        disableGradient('mainContent');
      }, 50);
    });
  };
  useEffect(() => {
    // keep glass overlay off by default
    contentGlassT.setValue(0);
  }, []);

  // Fetch saved queries
  useEffect(() => {
    const fetchSavedQueries = async () => {
      if (!user?.uid) {
        setSavedQueries([]);
        return;
      }
      
      try {
        const q = firestoreQuery(
          collection(db, 'saved_queries'),
          where('uid', '==', user.uid),
          orderBy('createdAt', 'desc'),
          limit(5)
        );
        const snapshot = await getDocs(q);
        const queries = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSavedQueries(queries);
      } catch (error) {
        console.error('[HomeScreen] Error fetching saved queries:', error);
      }
    };
    
    fetchSavedQueries();
  }, [user?.uid]);

  // Handle search start (show loading, then navigate) - only for foreground queries
  const handleSearchStart = (query, isBackground = false) => {
    console.log('[HomeScreen] Search starting for:', query, 'background:', isBackground);
    setSearchQuery(query);
    // Only show loading overlay for foreground queries
    if (!isBackground) {
    setIsSearching(true);
    }
    // Navigation will happen after the timeout in SearchInput
  };

  // Page load animation
  useEffect(() => {
    const resize = () => {
      const { width } = getViewport();
      setTitleSize(theme.layout.titleBase(width));
    };
    if (Platform.OS === 'web') {
      window.addEventListener('resize', resize);
    }
    resize();
    Animated.parallel([
      Animated.timing(pageOpacity, {
        toValue: 1,
        duration: 1000,
        easing: Easing.inOut(Easing.cubic),
        useNativeDriver: false,
      }),
      Animated.timing(pageTranslateY, {
        toValue: 0,
        duration: 1000,
        easing: Easing.inOut(Easing.cubic),
        useNativeDriver: false,
      }),
    ]).start();
  }, []);

  // Exit animation function
  const exitAnimation = (callback) => {
    setIsExiting(true);
    Animated.parallel([
      Animated.timing(pageOpacity, {
        toValue: 0,
        duration: 600,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: false,
      }),
      Animated.timing(pageTranslateY, {
        toValue: 20,
        duration: 600,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: false,
      }),
    ]).start(() => {
      if (callback) callback();
    });
  };

  // Register exit animation with navigation context
  useEffect(() => {
    // Store the exit animation function globally so navigation can access it
    if (Platform.OS === 'web') {
      window.homeExitAnimation = exitAnimation;
    }

    return () => {
      // Clean up on unmount
      if (Platform.OS === 'web') {
        delete window.homeExitAnimation;
      }
    };
  }, []);
  return (
    <View style={[styles.container, { backgroundColor: theme.colors.backgroundAlt }]}>
      {/* Background */}
      <View style={[styles.background, { backgroundColor: theme.colors.backgroundAlt }]} />
      
      {/* Splash Screen Container */}
      <View style={[styles.splashContainer, {
        paddingHorizontal: Platform.OS === 'web' ? theme.layout.pagePaddingX(bp.width) : 24,
        // Account for fixed header (navbar + ticker if enabled)
        paddingTop: Platform.OS === 'web' ? (totalHeaderHeight + theme.layout.pagePaddingY(bp.width)) : 16,
        paddingBottom: Platform.OS === 'web' ? theme.layout.pagePaddingY(bp.width) : 24,
        gap: theme.spacing(12)
      }]}>
        
        {/* Navigation Bar */}
        <NavigationHeader />
        
        {/* Main Content */}
        <Animated.View 
          style={[
            styles.mainContent,
            {
              opacity: pageOpacity,
              transform: [
                { translateY: pageTranslateY }
              ]
            },
            (Platform.OS === 'web' && (bp.isLg || bp.isXl)) ? { overflow: 'visible' } : null
          ]} 
          {...(Platform.OS === 'web' ? { 
            onMouseEnter: () => {
              onContentIn();
              Animated.timing(contentGlassT, { toValue: 1, duration: 300, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: false }).start();
            }, 
            onMouseLeave: () => {
              onContentOut();
              Animated.timing(contentGlassT, { toValue: 0, duration: 300, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: false }).start();
            } 
          } : {})}
        >
          <ThemeGlassBackground 
            opacity={contentGlassT} 
            borderRadius={styles.mainContent.borderRadius || theme.radii.lg} 
            enabled={isGradientEnabled('mainContent')} 
          />
          <ThemeGradientOverlay 
            opacity={contentGlowT} 
            borderRadius={styles.mainContent.borderRadius || theme.radii.lg} 
            direction="vertical" 
            enabled={isGradientEnabled('mainContent')} 
          />
          <View style={[
            styles.contentContainer,
            (Platform.OS === 'web' && (bp.isLg || bp.isXl)) ? { overflow: 'visible' } : null
          ]}>
            {/* Title */}
            <View style={styles.titleContainer}>
              <Text
                numberOfLines={bp.isXs || bp.isSm || bp.isMd ? 2 : 1}
                style={[
                  styles.title,
                  Platform.OS === 'web' ? { fontSize: titleSize, lineHeight: titleSize + 4 } : null
                ]}
              >BET OPTIMIZED</Text>
            </View>
            {/* Search Input */}
            <SearchInput 
              onNavigate={navigateTo} 
              valueProp={searchValue} 
              onValueChange={setSearchValue} 
              onSearchStart={(q) => handleSearchStart(q, backgroundQueryMode && canAddBackgroundQuery)}
              onExecuteSearch={async (q) => {
                // Check if background query mode is enabled
                if (backgroundQueryMode && canAddBackgroundQuery) {
                  // Just add to background queue - no loading overlay
                  await addBackgroundQuery(q, { league: 'nba', sport: 'basketball' });
                  // Clear the search input after adding to background
                  setSearchValue('');
                } else {
                  // Small delay to show loading state before navigation
                  setTimeout(() => {
                    navigateTo('SgpFlow', { payload: { query: q } });
                  }, 100);
                }
              }}
            />
            {/* Add Leg Button */}
            <AddLegButton onPress={async () => {
              const q = (searchValue || '').trim();
              if (q) {
                // Check if background query mode is enabled
                if (backgroundQueryMode && canAddBackgroundQuery) {
                  // Just add to background queue - no loading overlay
                  await addBackgroundQuery(q, { league: 'nba', sport: 'basketball' });
                  // Clear the search input after adding to background
                  setSearchValue('');
                } else {
                try { if (Platform.OS === 'web') window.sessionStorage.setItem('sgp:lastQuery', q); } catch {}
                navigateTo('SgpFlow', { payload: { query: q } });
                }
              }
            }} />
            
            {/* Saved Queries Section (subtle) */}
            {savedQueries.length > 0 && (
              <View style={{alignItems:'center', width: '100%', marginTop: 24}}>
                <TouchableOpacity
                  onPress={() => setShowSavedQueries(!showSavedQueries)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 6,
                    paddingVertical: 8,
                    paddingHorizontal: 12,
                    borderRadius: 8,
                    backgroundColor: 'rgba(255,255,255,0.03)',
                  }}
                >
                  <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                    {savedQueries.length} saved quer{savedQueries.length !== 1 ? 'ies' : 'y'}
                  </Text>
                  <Text style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                    {showSavedQueries ? '▼' : '▶'}
                  </Text>
                </TouchableOpacity>
                
                {showSavedQueries && (
                  <View style={{width: '100%', maxWidth: 600, marginTop: 12, gap: 8}}>
                    {savedQueries.map((sq) => (
                      <TouchableOpacity
                        key={sq.id}
                        onPress={async () => {
                          if (backgroundQueryMode && canAddBackgroundQuery) {
                            // Just add to background queue - no loading overlay
                            await addBackgroundQuery(sq.query, { league: 'nba', sport: 'basketball' });
                          } else {
                            navigateTo('SgpFlow', { payload: { query: sq.query } });
                          }
                        }}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          paddingVertical: 10,
                          paddingHorizontal: 14,
                          borderRadius: 8,
                          backgroundColor: 'rgba(255,255,255,0.04)',
                          borderWidth: 1,
                          borderColor: 'rgba(255,255,255,0.08)',
                        }}
                      >
                        <View style={{ flex: 1 }}>
                          <Text style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', fontWeight: '600' }}>
                            {sq.query}
                          </Text>
                          {sq.teamName && (
                            <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                              {sq.teamName} · {sq.fixtureCount} game{sq.fixtureCount !== 1 ? 's' : ''}
                            </Text>
                          )}
                        </View>
                        <TouchableOpacity
                          onPress={async (e) => {
                            e.stopPropagation();
                            try {
                              await deleteDoc(doc(db, 'saved_queries', sq.id));
                              setSavedQueries(prev => prev.filter(q => q.id !== sq.id));
                            } catch (error) {
                              console.error('Error deleting query:', error);
                            }
                          }}
                          style={{
                            padding: 6,
                            borderRadius: 6,
                            backgroundColor: 'rgba(255,255,255,0.05)',
                          }}
                        >
                          <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>✕</Text>
                        </TouchableOpacity>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>
            )}
          </View>
        </Animated.View>
        
      </View>
      
      {/* Loading Overlay - Shows during search initialization */}
      {isSearching && (
        <View style={styles.loadingOverlay}>
          <View style={styles.loadingContent}>
            <LoadingSpinner size={32} color="#a259ff" />
            <Text style={styles.loadingText}>Searching for {searchQuery}...</Text>
            <Text style={styles.loadingSubtext}>Checking cache...</Text>
          </View>
        </View>
      )}
    </View>
  );
}

const { width: screenWidth } = Dimensions.get('window');
const isVerySmall = screenWidth < 500; // Small phones (up to ~iPhone 14 Pro Max size)
const isMobile = screenWidth < 768;
const isTablet = screenWidth >= 768 && screenWidth < 1024;

// Responsive helper function with extra small breakpoint
const responsive = (verySmall, mobile, tablet, desktop) => {
  if (isVerySmall) return verySmall;
  if (isMobile) return mobile;
  if (isTablet) return tablet;
  return desktop;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#131313',
    minHeight: Platform.OS === 'web' ? '110vh' : '100%',
  },
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#131313',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
  },
  splashContainer: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
 
  // Main Content Styles
  mainContent: {
    flex: 1,
    width: '100%',
    borderRadius: responsive(12, 12, 16, 20),
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderBottomWidth: 2,
    borderTopColor: 'rgba(255,255,255,0.25)',
    borderLeftColor: 'rgba(255,255,255,0.18)',
    borderRightColor: 'rgba(255,255,255,0.12)',
    borderBottomColor: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
    paddingVertical: responsive(20, 24, 36, 48),
    paddingHorizontal: responsive(8, 12, 16, 0),
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentContainer: {
    width: '100%',
    maxWidth: 1280,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: responsive(4, 8, 12, 16),
    gap: responsive(12, 16, 20, 24),
    overflow: 'hidden',
  },
  
  // Title Styles
  titleContainer: {
    alignItems: 'center',
    gap: responsive(4, 6, 8, 10),
  },
  title: {
    fontFamily: 'ClimateCrisis',
    // Use viewport width (vw) units for responsive scaling on web
    fontSize: Platform.OS === 'web' 
      ? (isVerySmall ? '2.5vw' : isMobile ? '3vw' : isTablet ? '7.5vw' : '8vw')
      : responsive(12, 18, 56, 64),
    fontWeight: '400',
    // Line height proportional to font size
    lineHeight: Platform.OS === 'web'
      ? (isVerySmall ? '2.5vw' : isMobile ? '3vw' : isTablet ? '8.5vw' : '9vw')
      : responsive(12, 18, 56, 64
        
      ),
    textTransform: 'uppercase',
    textAlign: 'center',
    color: 'rgba(255,255,255,0.85)',
    maxWidth: '100%',
    wordWrap: 'break-word',
    overflowWrap: 'anywhere',
    marginLeft: responsive(0, 0, 8, 12),
    marginRight: responsive(0, 0, 8, 12),
    paddingHorizontal: responsive(4, 4, 0, 0),
    letterSpacing: responsive(-0.5, -0.5, -1.5, -1.5),
  },
  
  // Search Input Styles
  searchContainer: {
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 1200 : '100%',
    paddingVertical: responsive(12, 16, 24, 32),
    alignItems: 'center',
    gap: responsive(8, 8, 10, 12),
    overflow: 'hidden',
  },
  searchAnimatedWrap: {
    width: '100%',
    overflow: 'hidden',
  },
  searchInput: {
    width: '100%',
    height: responsive(48, 52, 56, 60),
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: responsive(12, 14, 17, 20),
    paddingVertical: responsive(8, 10, 12, 14),
    borderRadius: responsive(10, 12, 14, 16),
    borderWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.25)',
    borderLeftColor: 'rgba(255,255,255,0.18)',
    borderRightColor: 'rgba(0,0,0,0.35)',
    borderBottomColor: 'rgba(0,0,0,0.45)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    shadowColor: 'rgba(0, 0, 0, 0.25)',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 4,
  },
  // border radius is animated; keep for native parity if needed
  searchFocused: { },
  searchText: {
    flex: 1,
    fontFamily: 'Slate',
    fontSize: responsive(13, 14, 16, Platform.OS === 'web' ? 18 : 15),
    fontWeight: '600',
    lineHeight: responsive(16, 18, 20, Platform.OS === 'web' ? 22 : 20),
    color: 'rgba(255, 255, 255, 0.9)',
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  iconsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: responsive(10, 12, 16, 20),
  },
  
  // Button Styles
  buttonContainer: { marginTop: responsive(8, 12, 18, 24) },
  button: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: responsive(8, 10, 12, 15),
    paddingVertical: responsive(12, 14, 16, 18),
    paddingHorizontal: responsive(20, 24, 30, 36),
    borderRadius: responsive(10, 10, 11, 12),
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.25)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    shadowColor: 'rgba(0, 0, 0, 0.25)',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 4,
  },
  buttonPlus: {
    fontFamily: 'Outfit',
    fontSize: responsive(16, 18, 20, 22),
    fontWeight: '700',
    lineHeight: responsive(18, 20, 22, 24),
    color: '#FFF',
  },
  buttonText: {
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui, 'Segoe UI', Roboto, Inter, 'Helvetica Neue', Arial, sans-serif" : 'System',
    fontSize: responsive(14, 15, 16, 18),
    fontWeight: '800',
    lineHeight: responsive(16, 18, 20, 22),
    color: 'rgba(255,255,255,0.95)',
  },
  
  // Loading Overlay Styles
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  loadingContent: {
    alignItems: 'center',
    gap: 16,
    padding: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderWidth: 1,
    borderColor: 'rgba(162, 89, 255, 0.3)',
  },
  loadingText: {
    fontSize: 18,
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.9)',
    marginTop: 8,
  },
  loadingSubtext: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.6)',
  },
});
